import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { getSavedReferralCode, getFingerprint } from "@/pages/public/ReferralCapture";
import AuthShell from "@/components/AuthShell";
import { toast } from "sonner";
import { Eye, EyeOff, Check, X, RefreshCw, Hash, ImageIcon, PartyPopper } from "lucide-react";

const STEPS = ["Register", "Secret Number", "Secret Image", "Done"];

function strength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Za-z]/.test(pw) && /\d/.test(pw)) s++;
  if (pw.length >= 12) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}
const STR_LABELS = ["Too weak", "Weak", "Fair", "Good", "Strong"];
const STR_COLORS = ["#dc2626", "#f97316", "#fbbf24", "#22c55e", "#0B6B3A"];

function rand3() {
  const s = new Set();
  while (s.size < 3) s.add(Math.floor(Math.random() * 1000));
  return [...s];
}
function pick3(arr) {
  const c = [...arr];
  for (let i = c.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [c[i], c[j]] = [c[j], c[i]]; }
  return c.slice(0, 3);
}

export default function Register() {
  const { register, setupRecovery, user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(sp.get("step") === "recovery" ? 1 : 0);

  const [form, setForm] = useState({ name: "", mobile: "", password: "", confirm: "", referral_code: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    track("register_started");
    const c = getSavedReferralCode();
    if (c) setForm((f) => ({ ...f, referral_code: c }));
  }, []);

  const [numbers, setNumbers] = useState(rand3());
  const [chosenNum, setChosenNum] = useState(null);
  const [images, setImages] = useState([]);
  const [imgSet, setImgSet] = useState([]);
  const [chosenImg, setChosenImg] = useState(null);

  const loadImages = useCallback(async () => {
    const { data } = await api.get("/auth/recovery-images");
    setImgSet(data.images);
    setImages(pick3(data.images));
  }, []);

  useEffect(() => { if (step === 2 && imgSet.length === 0) loadImages(); }, [step, imgSet.length, loadImages]);

  const pwStr = strength(form.password);
  const match = form.confirm.length > 0 && form.confirm === form.password;

  const submitRegister = async (e) => {
    e.preventDefault(); setErr("");
    if (!/^[A-Za-z ]{3,60}$/.test(form.name.trim())) return setErr("Full name must be 3–60 letters and spaces.");
    if (!/^[6-9]\d{9}$/.test(form.mobile)) return setErr("Mobile must be 10 digits starting with 6, 7, 8 or 9.");
    if (!(form.password.length >= 8 && /[A-Za-z]/.test(form.password) && /\d/.test(form.password))) return setErr("Password needs 8+ chars with a letter and a number.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await register(form.name.trim(), form.mobile, form.password, { referral_code: form.referral_code.trim().toUpperCase(), fingerprint: getFingerprint() });
      toast.success("Account created! Now set up password recovery.");
      setStep(1);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const finishRecovery = async () => {
    setBusy(true);
    try {
      await setupRecovery(chosenNum, chosenImg);
      setStep(3);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const chosenImgMeta = imgSet.find((i) => i.id === chosenImg);

  return (
    <AuthShell title={step === 0 ? t("register") : "Password Recovery Setup"} subtitle={t("app_name")}>
      {/* Progress */}
      <div className="flex items-center justify-between mb-5" data-testid="register-progress">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold ${i < step ? "bg-thali-green text-white" : i === step ? "bg-thali-saffron text-white" : "bg-thali-cream text-thali-ink/40"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className="text-[10px] text-thali-ink/60 text-center w-14 leading-tight">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? "bg-thali-green" : "bg-thali-cream"}`} />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <form onSubmit={submitRegister} className="space-y-4">
          <div>
            <label className="font-semibold text-sm">{t("full_name")}</label>
            <input className="input-thali mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" />
          </div>
          <div>
            <label className="font-semibold text-sm">{t("mobile")}</label>
            <input className="input-thali mt-1" inputMode="numeric" pattern="[0-9]*" maxLength={10}
              value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} data-testid="reg-mobile" />
          </div>
          <div>
            <label className="font-semibold text-sm">{t("password")}</label>
            <div className="relative mt-1">
              <input className="input-thali pr-12" type={show ? "text" : "password"} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-thali-ink/50" aria-label="Toggle password">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {form.password && (
              <div className="mt-2" data-testid="pw-strength">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => <div key={i} className="h-1.5 flex-1 rounded-full" style={{ background: i < pwStr ? STR_COLORS[pwStr] : "#e5e0d0" }} />)}
                </div>
                <span className="text-xs font-semibold" style={{ color: STR_COLORS[pwStr] }}>{STR_LABELS[pwStr]}</span>
              </div>
            )}
          </div>
          <div>
            <label className="font-semibold text-sm">{t("confirm_password")}</label>
            <div className="relative mt-1">
              <input className="input-thali pr-10" type={show ? "text" : "password"} value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="reg-confirm" />
              {form.confirm && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="reg-match">
                  {match ? <Check className="w-5 h-5 text-green-600" /> : <X className="w-5 h-5 text-red-500" />}
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold">Referral code <span className="text-thali-ink/40 font-normal">(optional)</span></label>
            <input className="input-thali mt-1 uppercase" placeholder="e.g. SUBR4577" value={form.referral_code}
              onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} data-testid="reg-referral" />
          </div>
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-2" data-testid="reg-error">{err}</div>}
          <button disabled={busy} className="btn-3d-green w-full" data-testid="reg-submit">{busy ? "…" : t("register")}</button>
          <p className="text-center text-sm text-thali-ink/70">
            Already registered? <Link to="/login" className="text-thali-green font-bold" data-testid="reg-to-login">{t("login")}</Link>
          </p>
        </form>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="text-center">
            <Hash className="w-8 h-8 mx-auto text-thali-saffron mb-1" />
            <p className="text-sm text-thali-ink/70">Choose your <b>secret number</b>. Remember it — you will need it to recover your password.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {numbers.map((n) => (
              <button key={n} onClick={() => setChosenNum(n)} data-testid={`num-card-${n}`}
                className={`aspect-square rounded-2xl font-num font-extrabold text-3xl grid place-items-center transition-all ${chosenNum === n ? "bg-thali-saffron text-white ring-4 ring-thali-saffron/30 scale-105" : "bg-thali-cream text-thali-ink hover:bg-thali-turmeric/30"}`}>
                {String(n).padStart(3, "0")}
                {chosenNum === n && <Check className="w-5 h-5 absolute mt-16" />}
              </button>
            ))}
          </div>
          <button onClick={() => { setNumbers(rand3()); setChosenNum(null); }} className="btn-ghost w-full text-sm" data-testid="show-other-numbers">
            <RefreshCw className="w-4 h-4" /> Show other numbers
          </button>
          <button disabled={chosenNum === null} onClick={() => setStep(2)} className="btn-3d-green w-full" data-testid="num-next">Next</button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-thali-saffron mb-1" />
            <p className="text-sm text-thali-ink/70">Choose your <b>secret image</b>. Remember it too.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {images.map((img) => (
              <button key={img.id} onClick={() => setChosenImg(img.id)} data-testid={`img-card-${img.id}`}
                className={`aspect-square rounded-2xl grid place-items-center transition-all ${chosenImg === img.id ? "bg-thali-saffron/20 ring-4 ring-thali-saffron/40 scale-105" : "bg-thali-cream hover:bg-thali-turmeric/30"}`}>
                <span className="text-5xl">{img.emoji}</span>
              </button>
            ))}
          </div>
          <button onClick={() => { setImages(pick3(imgSet)); setChosenImg(null); }} className="btn-ghost w-full text-sm" data-testid="show-other-images">
            <RefreshCw className="w-4 h-4" /> Show other images
          </button>
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} className="btn-ghost flex-1">Back</button>
            <button disabled={!chosenImg || busy} onClick={finishRecovery} className="btn-3d-green flex-1" data-testid="img-finish">
              {busy ? "…" : "Confirm"}
            </button>
          </div>
          {chosenImg && (
            <div className="text-center text-sm bg-thali-cream rounded-xl p-3" data-testid="recovery-summary">
              Your number: <b>{String(chosenNum).padStart(3, "0")}</b> · Your image: <b>{chosenImgMeta?.emoji} {chosenImgMeta?.label}</b>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="text-center space-y-4 py-4">
          <PartyPopper className="w-14 h-14 mx-auto text-thali-green" />
          <h3 className="font-heading font-extrabold text-xl">All set!</h3>
          <p className="text-sm text-thali-ink/70">Your account and recovery are ready. Let's set up your school profile.</p>
          <button onClick={() => nav("/app/profile")} className="btn-3d-saffron w-full" data-testid="done-continue">Continue</button>
        </div>
      )}
    </AuthShell>
  );
}
